/**
 * <ActivationComposerTemplate> — le corps du composeur de la reprise (template).
 *
 * UN SEUL formulaire, UN SEUL envoi : formule, volume, matières, répartition, créneaux, date,
 * engagement. L'ordre est celui du legacy (`ActivationComposerScreen.js:752-1136`) — la classe
 * d'abord, le récapitulatif tarifaire en dernier.
 *
 * Entièrement contrôlé : chaque section reçoit ce qu'elle montre et rend le geste par un rappel.
 * Aucune règle métier ici — « la répartition a-t-elle un sens ? », « ce palier est-il vendu sans
 * engagement ? », « reste-t-il une date à proposer ? » sont des questions du view-model, et elles
 * arrivent ici sous forme de listes vides ou de rappels absents.
 *
 * Une section sans matière à montrer ne se rend pas : pas d'option, pas de section.
 */
import React from "react"

import { ActivationAllocationSection } from "../../organisms/ActivationAllocationSection"
import { ActivationChoiceSection } from "../../organisms/ActivationChoiceSection"
import { ActivationCommitmentSection } from "../../organisms/ActivationCommitmentSection"
import { ActivationPickerSection } from "../../organisms/ActivationPickerSection"
import { ActivationPriceSummary } from "../../organisms/ActivationPriceSummary"
import { ActivationResumeDateSection } from "../../organisms/ActivationResumeDateSection"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Stack } from "../../atoms/containers/Stack"
import {
    type UIActivationAllocationRow,
    type UIActivationChoice,
    type UIActivationPriceSummary,
    type UISubject,
    type UISubscriptionLine,
} from "../../../ports/primary/ui/types"

export type ActivationComposerTemplateProps = {
    // ── 1. Classe à la rentrée
    /** Le titre de la section - « Classe à la rentrée » par défaut. */
    classTitle?: string
    /** L'échelle des classes. Vide → la section ne se rend pas. */
    classOptions?: UIActivationChoice[]
    selectedClass?: string | null
    onSelectClass?: (value: string) => void

    // ── 2. Heures par semaine
    /** Les paliers de la grille. Vide → la section ne se rend pas. */
    hoursOptions?: UIActivationChoice<number>[]
    selectedHours?: number | null
    onSelectHours?: (value: number) => void
    /** La phrase du palier unique sans engagement. */
    hoursNote?: string | null

    // ── 3. Matières souhaitées
    selectedSubjects?: UISubject[]
    onOpenSubjects?: () => void

    // ── 4. Heures par matière (optionnelle)
    allocationRows?: UIActivationAllocationRow[]
    onChangeAllocation?: (subjectId: string | number, hours: number) => void
    /** Ouvrir la répartition au premier rendu — une story, ou une recette, peut la regarder. */
    allocationExpanded?: boolean

    // ── 5. Créneaux
    /** Combien de créneaux sont posés — 0 ou absent → « Choisir les créneaux ». */
    slotsCount?: number | null
    /** Les créneaux dits comme le parent les lit — « Lundi 17h-18h · Mercredi 18h-19h ». */
    slotsSummary?: string | null
    onOpenSlots?: () => void

    // ── 6. Date de reprise (au choix, ou verrouillée)
    resumeDateOptions?: UIActivationChoice[]
    /** Date libre : la première choisissable au calendrier. Absente → le choix parmi les options. */
    resumeDateMin?: Date | null
    selectedResumeDate?: string | null
    onSelectResumeDate?: (value: string) => void
    lockedResumeDateLine?: string | null
    lockedResumeDateNote?: string | null

    // ── 7. Mode de cours
    offerOptions?: UIActivationChoice[]
    selectedOffer?: string | null
    onSelectOffer?: (value: string) => void
    /** Le « i » de l'en-tête — sans rappel, pas de bouton. */
    onOpenOfferInfo?: () => void

    // ── 8. Durée d'engagement
    commitmentOptions?: UIActivationChoice<number>[]
    withCommitment?: boolean
    onChangeCommitment?: (next: boolean) => void
    guarantees?: UISubscriptionLine[]

    // ── 9. Récapitulatif tarifaire
    summary?: UIActivationPriceSummary | null

    testID?: string
}

export function ActivationComposerTemplate(props: ActivationComposerTemplateProps) {
    const {
        classTitle = "Classe à la rentrée",
        classOptions = [],
        selectedClass,
        onSelectClass,
        hoursOptions = [],
        selectedHours,
        onSelectHours,
        hoursNote,
        selectedSubjects = [],
        onOpenSubjects,
        allocationRows,
        onChangeAllocation,
        allocationExpanded,
        slotsCount,
        slotsSummary,
        onOpenSlots,
        resumeDateOptions,
        resumeDateMin,
        selectedResumeDate,
        onSelectResumeDate,
        lockedResumeDateLine,
        lockedResumeDateNote,
        offerOptions = [],
        selectedOffer,
        onSelectOffer,
        onOpenOfferInfo,
        commitmentOptions,
        withCommitment = true,
        onChangeCommitment,
        guarantees,
        summary,
        testID,
    } = props

    const slots = slotsCount ?? 0

    return (
        <Stack gap="6" testID={testID}>
            {classOptions.length > 0 && onSelectClass != null && (
                <ActivationChoiceSection
                    title={classTitle}
                    iconName="graduation-cap"
                    options={classOptions}
                    value={selectedClass}
                    onChange={onSelectClass}
                />
            )}

            {hoursOptions.length > 0 && onSelectHours != null && (
                <ActivationChoiceSection
                    title="Heures par semaine"
                    iconName="clock"
                    options={hoursOptions}
                    value={selectedHours}
                    onChange={onSelectHours}
                    note={hoursNote}
                />
            )}

            {onOpenSubjects != null && (
                <ActivationPickerSection
                    title="Matières souhaitées"
                    iconName="book"
                    placeholder="Choisir les matières"
                    subjects={selectedSubjects}
                    onPress={onOpenSubjects}
                />
            )}

            {onChangeAllocation != null && (
                <ActivationAllocationSection
                    rows={allocationRows}
                    onChange={onChangeAllocation}
                    defaultExpanded={allocationExpanded}
                />
            )}

            {onOpenSlots != null && (
                <ActivationPickerSection
                    title="Créneaux"
                    iconName="clock"
                    placeholder="Choisir les créneaux"
                    value={slots > 0 ? `${slots} créneau${slots > 1 ? "x" : ""} donné${slots > 1 ? "s" : ""}` : null}
                    detail={slots > 0 ? slotsSummary : null}
                    onPress={onOpenSlots}
                />
            )}

            <ActivationResumeDateSection
                options={resumeDateOptions}
                minDate={resumeDateMin}
                value={selectedResumeDate}
                onChange={onSelectResumeDate}
                lockedLine={lockedResumeDateLine}
                lockedNote={lockedResumeDateNote}
            />

            {offerOptions.length > 0 && onSelectOffer != null && (
                <ActivationChoiceSection
                    title="Mode de cours"
                    iconName="users"
                    options={offerOptions}
                    value={selectedOffer}
                    onChange={onSelectOffer}
                    headerRight={
                        onOpenOfferInfo != null && (
                            <IconButton
                                iconName="info-circle"
                                appearance="transparent"
                                iconColor="primary"
                                size={24}
                                onPress={onOpenOfferInfo}
                                accessibilityLabel="En savoir plus sur les modes de cours"
                            />
                        )
                    }
                />
            )}

            {onChangeCommitment != null && (
                <ActivationCommitmentSection
                    options={commitmentOptions}
                    withCommitment={withCommitment}
                    onChangeCommitment={onChangeCommitment}
                    guarantees={guarantees}
                />
            )}

            <ActivationPriceSummary summary={summary} />
        </Stack>
    )
}
